import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { createCompetition } from '@/lib/firebase/queries/competitions';
import type { CompetitionHabit } from '@/types/competitions';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { CompetitionHabitsEditor } from './CompetitionHabitsEditor';

// Create schemas as functions to access translations
const createCompetitionSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('dashboard.manager.competitions.validation.nameRequired') || 'Competition name is required'),
  description: z.string().optional(),
  startDate: z.date({
    required_error: t('dashboard.manager.competitions.validation.startDateRequired') || 'Start date is required',
  }),
  endDate: z.date({
    required_error: t('dashboard.manager.competitions.validation.endDateRequired') || 'End date is required',
  }),
}).refine((data) => data.endDate > data.startDate, {
  message: t('dashboard.manager.competitions.validation.endDateAfterStart') || 'End date must be after start date',
  path: ['endDate'],
});

interface CreateCompetitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  onSuccess?: () => void;
}

export function CreateCompetitionDialog({
  open,
  onOpenChange,
  organizationId,
  onSuccess
}: CreateCompetitionDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [habits, setHabits] = useState<CompetitionHabit[]>([]);
  const [isPublished, setIsPublished] = useState(true);
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();

  const competitionSchema = createCompetitionSchema(t);

  const form = useForm<z.infer<typeof competitionSchema>>({
    resolver: zodResolver(competitionSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof competitionSchema>) => {
    if (!currentUser) {
      toast.error(t('dashboard.manager.competitions.messages.loginRequired') || 'You must be logged in to create a competition');
      return;
    }

    if (habits.length === 0) {
      toast.error(t('dashboard.manager.competitions.messages.addHabitsRequired') || 'Please add at least one habit to the competition');
      return;
    }

    try {
      setIsLoading(true);

      await createCompetition({
        name: values.name,
        description: values.description,
        organizationId,
        startDate: values.startDate,
        endDate: values.endDate,
        habits,
        createdBy: currentUser.uid,
        isPublished,
      });

      toast.success(t('dashboard.manager.competitions.messages.createdSuccessfully') || 'Competition created successfully');
      onSuccess?.();
      onOpenChange(false);

      // Reset form
      form.reset();
      setHabits([]);
      setIsPublished(true);
    } catch (error) {
      console.error('Error creating competition:', error);
      
      // Check for specific error types
      if (error instanceof Error) {
        if (error.message.includes('Organization already has an active competition')) {
          toast.error(t('dashboard.manager.competitions.messages.activeCompetitionExists') || 'Organization already has an active competition. Only one active competition is allowed per organization.');
        } else if (error.message.includes('competition that overlaps with these dates')) {
          toast.error(t('dashboard.manager.competitions.messages.overlappingCompetitionExists') || 'Organization already has a competition that overlaps with these dates. Competitions cannot have overlapping time periods.');
        } else {
          toast.error(t('dashboard.manager.competitions.messages.createFailed') || 'Failed to create competition');
        }
      } else {
        toast.error(t('dashboard.manager.competitions.messages.createFailed') || 'Failed to create competition');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader>
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {t('dashboard.manager.competitions.createCompetition') || 'Create Competition'}
          </DialogTitle>
          <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {t('dashboard.manager.competitions.createCompetitionDesc') || 'Create a new competition for your organization members'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.manager.competitions.name') || 'Competition Name'}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('dashboard.manager.competitions.namePlaceholder') || 'Enter competition name'} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.manager.competitions.description') || 'Description'}</FormLabel>
                    <FormControl>
              <Textarea
                        placeholder={t('dashboard.manager.competitions.descriptionPlaceholder') || 'Enter competition description (optional)'}
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('dashboard.manager.competitions.startDate') || 'Start Date'}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                        <FormControl>
                  <Button
                    variant="outline"
                    className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                    )}
                  >
                            {field.value ? (
                              format(field.value, "PPP")
                    ) : (
                              <span>{t('dashboard.manager.competitions.pickStartDate') || 'Pick a start date'}</span>
                    )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                        </FormControl>
                </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const compareDate = new Date(date);
                            compareDate.setHours(0, 0, 0, 0);
                            return compareDate < today;
                          }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('dashboard.manager.competitions.endDate') || 'End Date'}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                        <FormControl>
                  <Button
                    variant="outline"
                    className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                    )}
                  >
                            {field.value ? (
                              format(field.value, "PPP")
                    ) : (
                              <span>{t('dashboard.manager.competitions.pickEndDate') || 'Pick an end date'}</span>
                    )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                        </FormControl>
                </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const compareDate = new Date(date);
                            compareDate.setHours(0, 0, 0, 0);
                            return compareDate < today;
                          }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </div>

          <CompetitionHabitsEditor habits={habits} onChange={setHabits} />

          {/* Publish visibility */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="create-competition-published" className="text-sm font-medium">
                {t('dashboard.manager.competitions.isPublishedLabel') || 'Visible to beneficiaries'}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('dashboard.manager.competitions.isPublishedDescription') || "Beneficiaries can see and join this competition once it's published."}
              </p>
            </div>
            <Switch
              id="create-competition-published"
              checked={isPublished}
              onCheckedChange={setIsPublished}
            />
          </div>

            <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
                {t('dashboard.manager.competitions.cancel') || 'Cancel'}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (t('dashboard.manager.competitions.creating') || 'Creating...') : (t('dashboard.manager.competitions.createCompetition') || 'Create Competition')}
            </Button>
            </DialogFooter>
        </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
